import { Link } from "react-router-dom";
import { ChevronDown, Phone } from "lucide-react";
import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { getSocialProfilesArray } from "@/config/social";
import { Logo } from "@/components/ui/Logo";
import elefaIcon from "@/assets/elefa-app-icon.webp";

const footerLinks = {
  connect: [
    { label: "International Calling", href: "/connect-portal" },
    { label: "Rates", href: "/connect-portal/rates" },
    { label: "Top Up", href: "/connect-portal/topup" },
    { label: "Referrals", href: "/referral" },
  ],
  support: [
    { label: "Help Centre", href: "/support" },
    { label: "About Us", href: "/about" },
  ],
  legal: [
    { label: "Privacy", href: "/legal/privacy" },
    { label: "Terms", href: "/legal/terms" },
    { label: "Fair Use", href: "/legal/fair-use" },
    { label: "Service Availability", href: "/legal/service-availability" },
    { label: "Complaints", href: "/legal/complaints" },
    { label: "Regulatory Info", href: "/regulatory" },
  ],
  contact: [
    { label: "General Support", href: "mailto:support@elefa.com.au" },
    { label: "Billing Enquiries", href: "mailto:billing@elefa.com.au" },
    { label: "Press & Media", href: "mailto:press@elefa.com.au" },
    { label: "Partnerships", href: "mailto:partnerships@elefa.com.au" },
  ],
};

interface FooterLinkSectionProps {
  title: string;
  links: { label: string; href: string }[];
}

function FooterLinkSection({ title, links }: FooterLinkSectionProps) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      {/* Mobile: Collapsible */}
      <Collapsible open={isOpen} onOpenChange={setIsOpen} className="lg:hidden">
        <CollapsibleTrigger className="flex w-full items-center justify-between py-3 text-sm font-semibold text-foreground">
          {title}
          <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
        </CollapsibleTrigger>
        <CollapsibleContent className="pb-3">
          <ul className="space-y-2 pl-2">
            {links.map((link) => (
              <li key={link.label}>
                <Link
                  to={link.href}
                  className="text-sm text-muted-foreground transition-colors hover:text-primary"
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
        </CollapsibleContent>
      </Collapsible>

      {/* Desktop: Always visible */}
      <div className="hidden lg:block">
        <h4 className="mb-3 text-sm font-semibold text-foreground">{title}</h4>
        <ul className="space-y-2">
          {links.map((link) => (
            <li key={link.label}>
              <Link
                to={link.href}
                className="text-sm text-muted-foreground transition-colors hover:text-primary"
              >
                {link.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </>
  );
}

interface FooterProps {
  hideQR?: boolean;
}

export function Footer({ hideQR = false }: FooterProps) {
  return (
    <footer className="border-t border-border bg-muted/30">
      <div className="container py-10">
        <div className="grid gap-6 lg:grid-cols-12 lg:gap-8">
          {/* Brand Column */}
          <div className="lg:col-span-3">
            <Logo variant="header" size="sm" withIcon fullName />
            <p className="mt-3 text-sm text-muted-foreground">
              International calling, worldwide. Top up once, dial anywhere.
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              {getSocialProfilesArray().map((social) => {
                const Icon = social.icon;
                return (
                  <a 
                    key={social.displayName}
                    href={social.url} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="rounded-lg bg-muted p-2 transition-colors hover:bg-primary hover:text-primary-foreground" 
                    aria-label={social.displayName}
                  >
                    <Icon className="h-4 w-4" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Links */}
          <div className="divide-y divide-border lg:col-span-6 lg:grid lg:grid-cols-4 lg:gap-6 lg:divide-y-0">
            <FooterLinkSection title="Connect" links={footerLinks.connect} />
            <FooterLinkSection title="Support" links={footerLinks.support} />
            <FooterLinkSection title="Legal" links={footerLinks.legal} />
            <FooterLinkSection title="Contact" links={footerLinks.contact} />
          </div>

          {/* QR Code Section */}
          {!hideQR && (
            <div className="flex flex-col items-center text-center lg:col-span-3 lg:items-end lg:text-right">
              <a 
                href="https://elefa.tel"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Open elefa.tel"
                className="group relative overflow-hidden rounded-2xl border-2 border-primary/20 bg-white p-3 shadow-lg transition-all hover:border-primary hover:shadow-xl"
              >
                <QRCodeSVG
                  value="https://elefa.tel"
                  size={120}
                  level="H"
                  includeMargin={false}
                  className="transition-transform group-hover:scale-105"
                  imageSettings={{
                    src: elefaIcon,
                    x: undefined,
                    y: undefined,
                    height: 30,
                    width: 30,
                    excavate: true,
                  }}
                />
                <div className="absolute -bottom-1 -right-1 rounded-full bg-primary p-1.5 shadow-md">
                  <Phone className="h-4 w-4 text-primary-foreground" />
                </div>
              </a>
              <p className="mt-3 text-sm font-medium text-foreground">
                Scan for elefa Connect
              </p>
              <p className="text-xs text-muted-foreground">
                International calling app
              </p>
            </div>
          )}
        </div>

        {/* Compact Bottom Bar */}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 border-t border-border pt-6 text-xs text-muted-foreground">
          <span>© {new Date().getFullYear()} elefa Mobile Pty Ltd</span>
          <span className="hidden sm:inline">·</span>
          <span>ABN 16 694 346 088</span>
          <span className="hidden sm:inline">·</span>
          <span>Melbourne, Australia</span>
        </div>
      </div>
    </footer>
  );
}
